import { motion } from "framer-motion";
import { Dumbbell, Heart, TrendingUp, Droplets, Brain, Zap } from "lucide-react";

const benefits = [
  {
    icon: Dumbbell,
    title: "Muscle Performance",
    description: "Supports high-intensity output and strength production.",
    span: "md:col-span-2 md:row-span-2",
    featured: true,
  },
  {
    icon: Heart,
    title: "Muscle Recovery",
    description: "Supports recovery between intense training sessions.",
    span: "md:col-span-1",
  },
  {
    icon: Brain,
    title: "Cognitive Function",
    description: "Supports neurological energy metabolism.",
    span: "md:col-span-1",
  },
  {
    icon: TrendingUp,
    title: "Muscle Growth",
    description: "Supports increased training volume and muscular adaptation.",
    span: "md:col-span-2",
  },
  {
    icon: Droplets,
    title: "Cellular Hydration",
    description: "Helps maintain intracellular hydration.",
    span: "md:col-span-1",
  },
  {
    icon: Zap,
    title: "Cellular Vitality",
    description: "Supports overall cellular energy availability.",
    span: "md:col-span-1",
  },
];

export const BenefitsGrid = () => {
  return (
    <section 
      id="benefits"
      className="py-24 md:py-32 bg-[#050505]"
      data-testid="benefits-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-mono text-sm tracking-[0.3em] text-[#E82D00] mb-4"
          >
            KEY BENEFITS
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="benefits-heading"
          >
            Performance Benefits
          </motion.h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`benefit-card relative overflow-hidden ${benefit.span}`}
              data-testid={`benefit-card-${index}`}
            >
              {/* Featured card extra styling */}
              {benefit.featured && (
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#E82D00]/10 blur-[60px]" />
              )}
              
              {/* Icon */}
              <div className={`icon-container mb-6 ${benefit.featured ? 'w-16 h-16' : ''}`}>
                <benefit.icon 
                  size={benefit.featured ? 32 : 24} 
                  strokeWidth={1.5} 
                  className="text-[#E82D00]" 
                />
              </div>

              {/* Title */}
              <h3 
                className={`font-bold uppercase tracking-tight text-white mb-3 ${
                  benefit.featured ? 'text-2xl md:text-3xl' : 'text-lg'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {benefit.title}
              </h3>

              {/* Description */}
              <p className={`text-[#A3A3A3] leading-relaxed ${
                benefit.featured ? 'text-lg' : 'text-sm'
              }`}>
                {benefit.description}
              </p>

              {/* Featured badge */}
              {benefit.featured && (
                <div className="absolute bottom-6 right-6">
                  <span className="font-mono text-xs tracking-widest text-[#E82D00]/50 uppercase">
                    Primary Benefit
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsGrid;
